import React, { useState, useEffect } from 'react';
import { Share2, TrendingUp, MessageCircle, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import BrushStrokeText from '../components/BrushStrokeText';
import BrandFrog from '../components/BrandFrog';
import EasterEggs from '../components/EasterEggs';
import CursorTrail from '../components/CursorTrail';
import TweetTyping from '../components/TweetTyping';
import FrogLoader from '../components/FrogLoader';
import { sendPartnershipEmail } from '../api/sendEmail';

const SprayPaintText = ({ text, delay = 0 }) => {
  const [displayedText, setDisplayedText] = useState(text);
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, text.length * 50 + delay);

    return () => clearTimeout(timer);
  }, [text, delay]);

  return (
    <div className="relative h-12 mb-8">
      <div className="relative z-10">
        {text.split('').map((letter, index) => (
          <span
            key={index}
            className={`inline-block text-green-400 font-graffiti ${isAnimating ? 'animate-spray' : ''}`}
            style={{
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              animationDelay: `${index * 50}ms`,
              opacity: isAnimating ? 0 : 1
            }}
          >
            {letter}
          </span>
        ))}
      </div>
      <div className="absolute -inset-1 bg-green-400/20 blur-lg -z-10"></div>
    </div>
  );
};

const MemeNetworkSite = () => {
  const [currentBrand, setCurrentBrand] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);

  const accounts = [
    {
      username: "forevertherapy",
      displayName: "FOREVER THERAPY",
      posts: "1,120",
      followers: "410K",
      instagramUrl: "https://instagram.com/forevertherapy"
    },
    {
      username: "hoesbewilin",
      displayName: "Hoes Be WILIN",
      posts: "2,013",
      followers: "448K",
      instagramUrl: "https://instagram.com/hoesbewilin"
    },
    {
      username: "animebewilin",
      displayName: "Anime Be WILIN",
      posts: "2,826",
      followers: "162K",
      instagramUrl: "https://instagram.com/animebewilin"
    },
    {
      username: "kanyesdiaryofficial",
      displayName: "Kanye's Diary",
      posts: "3,304",
      followers: "2.4M",
      instagramUrl: "https://instagram.com/kanyesdiaryofficial"
    }
  ];

  const nextBrand = () => {
    setCurrentBrand((prev) => (prev + 1) % accounts.length);
  };

  const prevBrand = () => {
    setCurrentBrand((prev) => (prev - 1 + accounts.length) % accounts.length);
  };

  const stats = [
    {
      icon: Users,
      value: '5M+',
      label: 'Community',
      description: 'Active Daily Users'
    },
    {
      icon: Share2,
      value: '200M+',
      label: 'Monthly Reach',
      description: 'Global Audience'
    },
    {
      icon: TrendingUp,
      value: '12%',
      label: 'Engagement',
      description: 'Industry Leading'
    }
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const scrollToPartner = (e) => {
    e.preventDefault();
    const partnerSection = document.getElementById('partner');
    partnerSection.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      console.log('Submitting form data:', formData);
      const result = await sendPartnershipEmail(formData);
      console.log('Email send result:', result);

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          company: '',
          message: ''
        });
        setShowSuccessScreen(true);
      } else {
        console.error('Email send failed:', result.error);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const SuccessScreen = () => (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="bg-black/80 border-2 border-green-500 rounded-xl p-8 max-w-lg w-full relative animate-fade-in">
        <div className="absolute -top-12 left-1/2 -translate-x-1/2">
          <div className="w-24 h-24 bg-black rounded-full border-4 border-green-500 flex items-center justify-center">
            <svg 
              className="w-12 h-12 text-green-500" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          </div>
        </div>
        
        <div className="text-center mt-8">
          <h2 className="text-3xl font-bold text-green-400 mb-4">
            Request Received!
          </h2>
          <p className="text-gray-300 text-lg mb-6">
            Thanks for reaching out. We'll review your partnership request and get back to you if there's a potential fit.
          </p>
          <button
            onClick={() => setShowSuccessScreen(false)}
            className="bg-green-500 text-black px-8 py-3 rounded-lg font-bold hover:bg-green-400 transition-colors"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return <FrogLoader />;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-green-900/40 via-black to-emerald-900/40">
      <div className="animated-bg" />
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(34,197,94,0.1),transparent)]"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-transparent transform rotate-45 scale-150"></div>
          <div className="absolute inset-0 bg-gradient-to-tl from-green-500/10 to-transparent transform rotate-45 scale-150"></div>
        </div>
        <div className="absolute inset-0 opacity-[0.015] mix-blend-overlay">
          <div className="absolute inset-0 bg-repeat bg-noise"></div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-32 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className="hero-text animate-fade-in mb-8">
                WILIN
                <br />
                WORLD
              </h1>
              <TweetTyping />
              <div className="pt-8 animate-fade-in" style={{ animationDelay: '3000ms' }}>
                <a 
                  href="#partner"
                  onClick={scrollToPartner}
                  className="group relative inline-flex items-center px-8 py-4 bg-green-500 overflow-hidden rounded-lg transition-all duration-300 hover:bg-green-600"
                >
                  <span className="absolute left-0 w-full h-full bg-black origin-left transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100"></span>
                  <span className="relative flex items-center text-xl font-bold text-black transition-colors duration-300 group-hover:text-green-500">
                    Partner With Us
                    <svg className="ml-2 w-6 h-6 transition-transform duration-300 group-hover:translate-x-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </a>
              </div>
            </div>
          </div>
          <div className="relative">
            <BrandFrog />
          </div>
        </div>
      </div>

      <div className="relative py-16 bg-gradient-to-b from-black via-green-900/20 to-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map(({ icon: Icon, value, label, description }) => (
              <div
                key={label}
                className="relative group bg-black/50 rounded-xl p-8 transform hover:-translate-y-2 transition-all duration-300 hover:bg-black/60 overflow-visible mt-4"
              >
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-500/20 via-green-400/20 to-green-500/20 blur-lg group-hover:opacity-100 opacity-0 transition-opacity duration-300 -z-10"></div>
                
                <div className="flex items-center justify-center mb-4">
                  <Icon className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-4xl font-bold text-center text-white mb-2">{value}</h3>
                <p className="text-green-400 font-semibold text-center mb-1">{label}</p>
                <p className="text-gray-400 text-sm text-center">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-screen-xl mx-auto">
          <SprayPaintText text="OUR BRANDS" />
          <div className="mt-6 overflow-x-auto">
            <div className="flex space-x-6 min-w-max pb-4">
              {accounts.map((account, index) => (
                <a 
                  key={index}
                  href={account.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-black/80 border-2 border-green-500/30 rounded-xl p-8 hover:border-green-500 transition-all w-[280px] cursor-pointer transform-gpu hover:scale-105 hover:bg-green-900/20"
                >
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-green-400 group-hover:text-green-300 transition-colors">
                      @{account.username}
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-black/40 rounded-lg group-hover:bg-black/60 transition-colors">
                      <p className="text-white font-bold text-xl group-hover:text-green-400 transition-colors">
                        {account.posts}
                      </p>
                      <p className="text-gray-400 text-sm">posts</p>
                    </div>
                    <div className="text-center p-4 bg-black/40 rounded-lg group-hover:bg-black/60 transition-colors">
                      <p className="text-white font-bold text-xl group-hover:text-green-400 transition-colors">
                        {account.followers}
                      </p>
                      <p className="text-gray-400 text-sm">followers</p>
                    </div>
                  </div>
                  <div className="mt-6 text-center">
                    <span className="inline-flex items-center text-gray-400 group-hover:text-green-400 transition-colors">
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      View Profile
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div id="partner" className="container mx-auto px-4 py-16 scroll-mt-16">
        <div className="max-w-2xl mx-auto">
          <div className="relative mb-8">
            <SprayPaintText text="Partner With Us" />
          </div>
          <div className="relative z-20">
            <form onSubmit={handleSubmit} className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 opacity-75 blur-xl group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative space-y-6 bg-black p-8 rounded-xl border-2 border-green-500/30">
                <div>
                  <label className="block text-green-400 mb-2 font-bold text-lg">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-4 bg-green-900/20 border-2 border-green-500/30 rounded-lg text-white focus:border-green-400 focus:ring-1 focus:ring-green-400 text-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-green-400 mb-2 font-bold text-lg">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full p-4 bg-green-900/20 border-2 border-green-500/30 rounded-lg text-white focus:border-green-400 focus:ring-1 focus:ring-green-400 text-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-green-400 mb-2 font-bold text-lg">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full p-4 bg-green-900/20 border-2 border-green-500/30 rounded-lg text-white focus:border-green-400 focus:ring-1 focus:ring-green-400 text-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-green-400 mb-2 font-bold text-lg">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full p-4 bg-green-900/20 border-2 border-green-500/30 rounded-lg text-white focus:border-green-400 focus:ring-1 focus:ring-green-400 text-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-green-400 mb-2 font-bold text-lg">Message</label>
                  <textarea
                    rows="4"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full p-4 bg-green-900/20 border-2 border-green-500/30 rounded-lg text-white focus:border-green-400 focus:ring-1 focus:ring-green-400 text-lg"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 text-white py-4 px-8 rounded-lg text-xl font-bold transition-all flex items-center justify-center space-x-2 transform hover:scale-105 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90'}`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    <span>Submit</span>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="text-green-400 text-center font-bold animate-fade-in">
                    Thank you! We'll be in touch soon.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="text-red-400 text-center font-bold animate-fade-in">
                    Something went wrong. Please try again.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <EasterEggs />
      <CursorTrail />
      {showSuccessScreen && <SuccessScreen />}
    </div>
  );
};

export default MemeNetworkSite